/**
 * S04 brand panel — "Switching, simplified. Your first invoice doesn't wait
 * for data migration."
 *
 * Kept as its own file (rather than collapsed into `brand-content.tsx` with
 * the other four screens) because it carries the screen-specific
 * `<MigrationPreview>` card — sample stats + sky-fill confidence rows that no
 * other screen uses.
 *
 * Mirrored from `prototype/onboarding/tally.html`.
 */
import { List, Plus, Upload } from "lucide-react";
import { ProgressBar, MetaPill } from "@flint/ui";
import { BrandPanel, StoryList } from "../index.ts";

export function TallyBrand() {
  return (
    <BrandPanel
      eyebrow="Switching, simplified"
      title="Your first invoice doesn't wait for data migration."
      subtitle="Flint treats Tally migration as a trust-building import path, not a setup trap. Bring history when you need it. Start operating today."
      source="Built in-house migration path · White-glove for design partners first"
    >
      <MigrationPreview />

      <StoryList
        items={[
          {
            icon: <Upload size={14} />,
            title: "Background parse, not blocked setup",
            description:
              "The real flow uploads first, then maps and reviews in the background so the founder can keep moving.",
          },
          {
            icon: <List size={14} />,
            title: "Confidence before import",
            description:
              "Anything ambiguous is surfaced as reviewable, with field-level confidence, instead of quietly imported as truth.",
          },
          {
            icon: <Plus size={14} />,
            title: "Numbering continuity stays intact",
            description:
              "Document series picks up from the migrated state, so founders don’t have to rebuild invoice history manually.",
          },
        ]}
      />
    </BrandPanel>
  );
}

function MigrationPreview() {
  return (
    <div className="flex flex-col gap-3.5 p-4 rounded bg-paper border border-line shadow-card max-w-[372px]">
      <div className="flex justify-between items-start gap-3">
        <div className="flex flex-col gap-0.5">
          <strong className="text-sm leading-[18px] font-semibold text-ink">
            Import preview
          </strong>
          <span className="text-xs leading-[15px] text-muted">
            Sharma Industries · FY 2025–26
          </span>
        </div>
        <MetaPill tone="ok">Reviewable</MetaPill>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Entries", value: "12,847" },
          { label: "Review", value: "47" },
          {
            label: "Ready",
            value: (
              <>
                99<span className="text-xs text-muted ml-px">%</span>
              </>
            ),
          },
        ].map((s) => (
          <div key={s.label} className="pt-2.5 border-t border-line">
            <span className="block text-xs uppercase tracking-[0.14em] text-muted mb-1">
              {s.label}
            </span>
            <span className="block font-mono tabular-nums text-lg leading-[22px] font-semibold tracking-[-0.01em] text-ink">
              {s.value}
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2.5">
        {[
          { label: "Customers", pct: 96 },
          { label: "Vendors", pct: 93 },
          { label: "Invoices", pct: 98 },
        ].map((row) => (
          <div
            key={row.label}
            className="grid grid-cols-[92px_1fr_auto] items-center gap-2.5 text-xs text-ink-2"
          >
            <span>{row.label}</span>
            <ProgressBar value={row.pct} />
            <span className="font-mono tabular-nums text-xs text-muted">{row.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
